import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, ShieldCheck, Layers, Lock } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';
import { Loading, EmptyState, UnavailableState } from '@/components/eds';
import MapViewer, { type MapLayerView } from '@/components/portal/gis/MapViewer';
import { publicGisService, type PublicGisLayer } from '@/services/publicGisService';
import type { GeoClassification } from '@/services/gisService';

// R11P GEOLOGICAL P0.5-P9C — anonymous Public GIS workspace.
//
// Shows ONLY explicitly publicly-disclosed spatial layers (enforced server-side;
// this page fetches via the uncredentialed publicGisService). The map is
// sovereign/overlays-only — no third-party basemap/tile request. Internal
// geological approval or publication does NOT make a layer public here.
//
// UNAVAILABLE ≠ ZERO: a failed request renders "temporarily unavailable", never
// a false "no layers". Analyst/investor GIS (authenticated) lives elsewhere.

type ViewState = 'loading' | 'ready' | 'empty' | 'unavailable';

const PublicGis: React.FC = () => {
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [layers, setLayers] = useState<PublicGisLayer[]>([]);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    publicGisService.listPublicLayers().then((res) => {
      if (cancelled) return;
      if (res.error || res.data === null) {
        setViewState('unavailable'); // transport/5xx — NOT a zero result
        return;
      }
      setLayers(res.data);
      setViewState(res.data.length === 0 ? 'empty' : 'ready');
    });
    return () => { cancelled = true; };
  }, []);

  // Project the public catalogue onto the viewer's layer shape (public title +
  // geometry/classification only). restrictedLayers is always empty here.
  const mapLayers = useMemo<MapLayerView[]>(
    () => layers.map((l) => ({
      id: l.id,
      name: l.title,
      category: '',
      permitted: true,
      classification: l.classification as GeoClassification,
      geometryType: l.geometryType,
      description: l.description,
    })),
    [layers],
  );

  const onToggle = useCallback((id: string) => {
    setVisibility((v) => ({ ...v, [id]: !(v[id] ?? true) }));
  }, []);

  const showWorkspace = viewState === 'ready' || viewState === 'empty';

  return (
    <PortalShell>
      <PublicHero
        badge="Public GIS"
        title={<>Public spatial<br /><span className="text-eds-mint">information</span></>}
        subtitle="Explore publicly disclosed GIISDEC spatial layers. Public GIS shows only information that has been formally released for public access."
      />

      <section className="py-10 md:py-14 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Concise disclosure explainer (§34) */}
          <div className="rounded-eds-md border border-eds-border bg-eds-surface-2 px-4 py-3 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-eds-emerald-2 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="font-eds-body text-sm text-eds-muted-fg">
              Public GIS contains publicly disclosed information only. Internal geological
              approval or internal GIS publication does not automatically make a layer public —
              public release is governed and authorised by GIISDEC.
            </p>
          </div>

          {viewState === 'loading' && (
            <Loading label="Loading public spatial layers…" />
          )}

          {viewState === 'unavailable' && (
            <UnavailableState
              title="Public GIS temporarily unavailable"
              message="The public spatial service could not be reached. Please try again shortly."
            />
          )}

          {showWorkspace && (
            <>
              <MapViewer
                layers={mapLayers}
                restrictedLayers={[]}
                visibility={visibility}
                onToggle={onToggle}
                featureLoader={publicGisService.getPublicLayerFeatures}
              />

              {viewState === 'empty' && (
                <EmptyState
                  title="No public spatial layers are currently available"
                  message="Spatial data publication is governed by GIISDEC. Publicly disclosed layers will appear here as datasets are formally released for public access."
                />
              )}

              {/* Accessible, non-map catalogue of public layers (§37) */}
              {viewState === 'ready' && (
                <div>
                  <h2 className="font-eds-display text-lg font-semibold text-eds-fg flex items-center gap-2">
                    <Layers className="w-5 h-5 text-eds-emerald-2" aria-hidden="true" /> Public layer catalogue
                  </h2>
                  <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {layers.map((l) => (
                      <li key={l.id} className="rounded-eds-md border border-eds-border bg-eds-surface p-4">
                        <h3 className="font-eds-display font-semibold text-eds-fg">{l.title}</h3>
                        {l.description && (
                          <p className="mt-1 font-eds-body text-sm text-eds-muted-fg">{l.description}</p>
                        )}
                        <dl className="mt-2 font-eds-body text-[11px] uppercase tracking-wider text-eds-muted-fg space-y-0.5">
                          <div className="flex gap-1"><dt>Geometry:</dt><dd>{l.geometryType ?? 'GeoJSON'}</dd></div>
                          <div className="flex gap-1"><dt>Classification:</dt><dd>{l.classification}</dd></div>
                        </dl>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Separate authenticated GIS entry — not required to view public layers (§8) */}
          <div className="flex items-center gap-2 pt-2 font-eds-body text-sm text-eds-muted-fg">
            <Lock className="w-4 h-4" aria-hidden="true" />
            <span>
              GIS analysts and authorised staff:{' '}
              <Link to="/gis-login" className="font-semibold text-eds-emerald-2 hover:underline">
                sign in to the GIS workspace
              </Link>
              .
            </span>
          </div>
        </div>
      </section>
    </PortalShell>
  );
};

export default PublicGis;
